import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2, Eye, FileText } from 'lucide-react';
import { useAdminCourses } from '@/presentation/hook/useAdminCourses';
import { useAdminChapters } from '@/presentation/hook/useAdminChapters';
import { Btn, Alert, Chip, MultilineText } from '@/presentation/component/ui';
import { inputClass } from '@/presentation/component/formStyles';
import { truncate } from '@/presentation/util/text';
import { formatDuration } from '@/presentation/util/format';
import { mediaTypeLabelKey } from '@/presentation/util/mediaType';
import { AdminBreadcrumb } from '@/presentation/component/admin/AdminBreadcrumb';
import { CourseFormModal, type CourseFormData } from './CourseFormModal';
import { CourseDeleteModal } from './CourseDeleteModal';
import type { AdminCourse } from '@/domain/model/AdminCourse';
import type { CourseUploadFiles } from '@/domain/model/CourseUploadFiles';
import type { UpdateCourseRequest } from '@/application/dto/UpdateCourseRequest';

interface CourseDocumentsCountProps {
  count: number;
}

const CourseDocumentsCount = ({ count }: CourseDocumentsCountProps) => {
  const { t } = useTranslation();

  if (count === 0) {
    return <span className="text-[var(--color-ink-faint)]">—</span>;
  }

  return (
    <Chip>
      <FileText className="mr-1 inline h-3 w-3" />
      {t('admin.documentsCount', { count })}
    </Chip>
  );
};

export const AdminCoursesPage = () => {
  const { t } = useTranslation();
  const { chapterId: parentId } = useParams<{ chapterId: string }>();
  const isDrillDown = !!parentId;

  const {
    courses,
    isLoading,
    uploadProgress,
    error,
    fetchCourses,
    createCourse,
    updateCourse,
    deleteCourse,
    removeCourseDocument,
    clearError,
  } = useAdminCourses();
  const { chapters, fetchChapters } = useAdminChapters();

  const [selectedChapterId, setSelectedChapterId] = useState<string>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<AdminCourse | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const activeFilter = isDrillDown ? parentId : selectedChapterId;
  const parentChapter = isDrillDown ? chapters.find((c) => c.id === parentId) : null;

  useEffect(() => {
    fetchChapters();
  }, [fetchChapters]);

  useEffect(() => {
    fetchCourses(activeFilter || undefined);
  }, [fetchCourses, activeFilter]);

  const handleChapterFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedChapterId(e.target.value);
  };

  const openCreateForm = () => {
    setEditingCourse(null);
    setIsFormOpen(true);
  };

  const openEditForm = (course: AdminCourse) => {
    setEditingCourse(course);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingCourse(null);
  };

  const handleFormSubmit = async (data: CourseFormData, files: CourseUploadFiles) => {
    let success: boolean;
    if (editingCourse) {
      const updateData: UpdateCourseRequest = {
        ...data,
        displayOrder: data.displayOrder ?? editingCourse.displayOrder,
      };
      success = await updateCourse(editingCourse.id, updateData, files);
      if (success) toast.success(t('admin.courseUpdated'));
    } else {
      if (!files.media) return;
      success = await createCourse(data, { ...files, media: files.media });
      if (success) toast.success(t('admin.courseCreated'));
    }
    if (success) {
      handleCloseForm();
      fetchCourses(activeFilter || undefined);
    }
  };

  const handleRemoveDocument = async (documentId: string) => {
    if (!editingCourse) return false;
    const success = await removeCourseDocument(editingCourse.id, documentId);
    if (!success) return false;
    toast.success(t('admin.documentRemoved'));
    fetchCourses(activeFilter || undefined);
    return true;
  };

  const handleConfirmDelete = async () => {
    if (deletingId) {
      const success = await deleteCourse(deletingId);
      if (success) {
        toast.success(t('admin.courseDeleted'));
        fetchCourses(activeFilter || undefined);
      }
      setDeletingId(null);
    }
  };

  const isUploading = uploadProgress !== null;

  const filteredChapters =
    isDrillDown && parentId ? chapters.filter((ch) => ch.id === parentId) : chapters;

  return (
    <div>
      {isDrillDown && (
        <AdminBreadcrumb
          items={[
            { label: t('admin.chaptersNav'), to: '/admin/chapters' },
            { label: parentChapter?.name ?? '...' },
          ]}
        />
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-ink)]">{t('admin.courses')}</h1>
          <p className="text-sm text-[var(--color-ink-faint)]">
            {courses.length} {t('admin.courses').toLowerCase()}
          </p>
        </div>
        <Btn kind="primary" icon={<Plus className="h-4 w-4" />} onClick={openCreateForm}>
          {t('admin.addCourse')}
        </Btn>
      </div>

      {!isDrillDown && (
        <div className="mt-4">
          <label htmlFor="chapter-filter" className="sr-only">
            {t('admin.parentChapter')}
          </label>
          <select
            id="chapter-filter"
            value={selectedChapterId}
            onChange={handleChapterFilter}
            className={`${inputClass()} sm:w-64`}
          >
            <option value="">{t('admin.allChapters')}</option>
            {chapters.map((ch) => (
              <option key={ch.id} value={ch.id}>
                {ch.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <Alert kind="bad" onClose={clearError} className="mt-4">
          {t(error)}
        </Alert>
      )}

      {isLoading && !isUploading && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">{t('common.loading')}</p>
      )}

      {!isLoading && courses.length === 0 && !error && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">{t('admin.noCourses')}</p>
      )}

      {courses.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="mt-6 hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line)] text-[var(--color-ink-faint)]">
                  <th className="pb-3 pr-4 font-medium">{t('admin.courseTitle')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.courseDescription')}</th>
                  {!isDrillDown && (
                    <th className="pb-3 pr-4 font-medium">{t('admin.parentChapter')}</th>
                  )}
                  <th className="pb-3 pr-4 font-medium">{t('admin.mediaType')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.courseDocuments')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.duration')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.displayOrder')}</th>
                  <th className="pb-3 font-medium">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => (
                  <tr
                    key={course.id}
                    className="border-b border-[var(--color-line)] text-[var(--color-ink)]"
                  >
                    <td className="py-3 pr-4 font-medium">
                      <div className="flex items-center gap-2">
                        {course.title}
                        {course.preview && (
                          <Chip kind="accent">
                            <Eye className="mr-1 inline h-3 w-3" />
                            {t('admin.preview')}
                          </Chip>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <MultilineText className="text-[var(--color-ink-soft)]">
                        {truncate(course.description, 60)}
                      </MultilineText>
                    </td>
                    {!isDrillDown && <td className="py-3 pr-4">{course.chapterName}</td>}
                    <td className="py-3 pr-4">
                      {course.mediaType ? (
                        <Chip>{t(mediaTypeLabelKey(course.mediaType))}</Chip>
                      ) : (
                        <span className="text-[var(--color-ink-faint)]">—</span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <CourseDocumentsCount count={course.documents.length} />
                    </td>
                    <td className="py-3 pr-4">{formatDuration(course.duration)}</td>
                    <td className="py-3 pr-4">{course.displayOrder}</td>
                    <td className="py-3">
                      <div className="flex gap-1">
                        <Btn
                          size="sm"
                          kind="ghost"
                          aria-label={t('admin.editCourse')}
                          onClick={() => openEditForm(course)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Btn>
                        <Btn
                          size="sm"
                          kind="ghost"
                          aria-label={t('admin.deleteCourse')}
                          onClick={() => setDeletingId(course.id)}
                        >
                          <Trash2 className="h-4 w-4 text-[var(--color-bad)]" />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mt-6 space-y-3 md:hidden">
            {courses.map((course) => (
              <div
                key={course.id}
                className="rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-[var(--color-ink)]">{course.title}</p>
                      {course.preview && (
                        <Chip kind="accent">
                          <Eye className="mr-1 inline h-3 w-3" />
                          {t('admin.preview')}
                        </Chip>
                      )}
                    </div>
                    <MultilineText className="mt-1 text-sm text-[var(--color-ink-soft)]">
                      {course.description}
                    </MultilineText>
                  </div>
                  <div className="ml-2 flex gap-1">
                    <Btn
                      size="sm"
                      kind="ghost"
                      aria-label={t('admin.editCourse')}
                      onClick={() => openEditForm(course)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Btn>
                    <Btn
                      size="sm"
                      kind="ghost"
                      aria-label={t('admin.deleteCourse')}
                      onClick={() => setDeletingId(course.id)}
                    >
                      <Trash2 className="h-4 w-4 text-[var(--color-bad)]" />
                    </Btn>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--color-ink-faint)]">
                  {!isDrillDown && <span>{course.chapterName}</span>}
                  {course.mediaType && <Chip>{t(mediaTypeLabelKey(course.mediaType))}</Chip>}
                  <CourseDocumentsCount count={course.documents.length} />
                  <span>{formatDuration(course.duration)}</span>
                  <span>
                    {t('admin.displayOrder')}: {course.displayOrder}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <CourseFormModal
        open={isFormOpen}
        editingCourse={editingCourse}
        chapters={filteredChapters}
        isLoading={isLoading}
        uploadProgress={uploadProgress}
        onSubmit={handleFormSubmit}
        onRemoveDocument={handleRemoveDocument}
        onClose={handleCloseForm}
      />

      <CourseDeleteModal
        open={deletingId !== null}
        isLoading={isLoading}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeletingId(null)}
      />
    </div>
  );
};
